import { gql } from "graphql-request";
import type { GetStaticPaths, GetStaticProps } from "next";
import BlogDetails from "~/components/blog-details";
import Metadata from "~/components/metadata";
import StructuredData from "~/components/structured-data";
import client from "~/config/client";
import type { PostCoreDataFragment } from "~/__generated__/graphql";
import { getSdk } from "~/__generated__/graphql";

type Props = {
  data: PostCoreDataFragment;
};

type Params = {
  slug: string;
};

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  const { posts } = await client.request<{
    posts: { data: { attributes: { slug: string } }[] };
  }>(gql`
    query Posts {
      posts(sort: "createdAt:desc", pagination: { start: 0, limit: 10 }, publicationState: LIVE) {
        data {
          attributes {
            slug
          }
        }
      }
    }
  `);

  const paths = posts.data.map(({ attributes: { slug } }) => ({ params: { slug } }));

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps<Props, Params> = async ({ params }) => {
  const { posts } = await getSdk(client).Posts({
    pagination: {
      limit: 1,
    },
    filters: {
      slug: {
        eq: params?.slug,
      },
    },
  });

  const data = posts?.data.at(0);

  if (!data) return { notFound: true };

  return { props: { data }, revalidate: 1 };
};

export default function BlogDetailsPage(props: Props) {
  return (
    <>
      <Metadata
        title={props.data.attributes?.title}
        image={props.data.attributes?.cover.data?.attributes?.url}
        canonical={`https://advante.co/blog/${props.data.attributes?.slug}`}
        description={props.data.attributes?.description ?? ""}
      />

      <StructuredData
        data={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://advante.co/blog/${props.data.attributes?.slug}`,
          },
          headline: props.data.attributes?.title ?? "",
          image: props.data.attributes?.cover.data?.attributes?.url ?? "",
          author: {
            "@type": "Person",
            name: props.data.attributes?.author?.data?.attributes?.name ?? "",
            url:
              props.data.attributes?.author?.data?.attributes?.avatar?.data?.attributes?.url ?? "",
          },
          publisher: {
            "@type": "Organization",
            name: "Advante",
            logo: {
              "@type": "ImageObject",
              url: "https://advante.co/images/advante-logo.webp",
            },
          },
          datePublished: props.data.attributes?.publishedAt,
        }}
      />

      <BlogDetails {...props} />
    </>
  );
}
